import {
  isDebugWebrtcLoggingEnabled,
  signalLevelForLatencyMs,
  type StreamSignalQualityLevel,
} from '@playcastdotio/PeerCore';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';

/**
 * How many recent calibrated samples the published level is the median of. Calibrated
 * readings are quantised by frame timing (~17 ms at 60 fps), so a single sample sitting on
 * a threshold would flicker the bars every report; the median of a short window moves only
 * on a sustained change and still follows one within a few seconds at the 1 Hz report rate.
 */
export const SIGNAL_LATENCY_SMOOTHING_SAMPLES = 5;

export type SignalQualityPublisherDeps = {
  /**
   * Deliver a level to the guest's HUD. Return `false` when it could not be delivered (the
   * data channel is not open yet) so the level stays unlatched and `resend` retries it.
   * `void` is treated as delivered.
   */
  send: (peerId: string, level: StreamSignalQualityLevel) => boolean | void;
};

type PeerSignal = {
  /** Most recent calibrated readings, oldest first. */
  window: number[];
  /** The level the latest smoothed reading maps to. */
  measured: StreamSignalQualityLevel;
  /** The level the guest is known to have received, if any. */
  delivered: StreamSignalQualityLevel | null;
};

/**
 * Publishes the guest HUD's signal-quality level from the PLAYER-VISIBLE LATENCY.
 *
 * The bars are a coarse reading of the calibrated glass-to-glass latency — how long the
 * player waits to see a frame — not of the encoder's recovery state, which used to drive
 * them (a hard cut on a clean nearby link showed one red bar while the player felt nothing,
 * and a distant guest showed three green bars at 200 ms). Every trusted calibrated sample is
 * fed here; the level is the median of the last few so a frame of quantisation noise cannot
 * flicker the bars.
 *
 * Delivery latches only on a confirmed send — congestion monitoring and the latency probe
 * both start before the data channel exists — and `resend` re-delivers the last measured
 * level unconditionally, because a (re)opened channel has lost whatever the guest had.
 * Before any reading exists nothing is sent: the guest draws no bars until a measurement
 * has been taken.
 */
export class SignalQualityPublisher {
  private readonly peers = new Map<string, PeerSignal>();

  constructor(private readonly deps: SignalQualityPublisherDeps) {}

  /** Feed one trusted calibrated glass-to-glass reading for a peer. */
  publishLatency(peerId: string, latencyMs: number): StreamSignalQualityLevel | null {
    if (!Number.isFinite(latencyMs) || latencyMs < 0) return null;
    const peer = this.peerFor(peerId);
    peer.window.push(latencyMs);
    while (peer.window.length > SIGNAL_LATENCY_SMOOTHING_SAMPLES) peer.window.shift();

    const level = signalLevelForLatencyMs(median(peer.window));
    if (level === 'none') return null;
    peer.measured = level;
    this.deliver(peerId, peer, level, /* force */ false);
    return level;
  }

  /**
   * Re-deliver the last measured level, e.g. once the peer's data channel (re)opens. A peer
   * with no reading yet sends nothing — the HUD stays empty until one exists.
   */
  resend(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer || peer.measured === 'none') return;
    this.deliver(peerId, peer, peer.measured, /* force */ true);
  }

  /** The level the latest smoothed reading maps to, or null before any reading. */
  levelFor(peerId: string): StreamSignalQualityLevel | null {
    const peer = this.peers.get(peerId);
    return !peer || peer.measured === 'none' ? null : peer.measured;
  }

  clearPeer(peerId: string): void {
    this.peers.delete(peerId);
  }

  clearAll(): void {
    this.peers.clear();
  }

  /**
   * Latches only on a confirmed send. Latching first meant a level sent into a channel that
   * was not open yet was dropped and never retried, because the publisher believed the guest
   * already had it.
   */
  private deliver(
    peerId: string,
    peer: PeerSignal,
    level: StreamSignalQualityLevel,
    force: boolean,
  ): void {
    if (!force && peer.delivered === level) return;
    // DEBUG BUILD ONLY — what the guest's HUD should show right now, before delivery.
    if (isDebugWebrtcLoggingEnabled()) {
      const from = peer.delivered ?? '(none)';
      console.log('[SignalQuality.level]', { peerId, level, from, force });
      sinkWebrtcDebugLine(
        JSON.stringify({ t: new Date().toISOString(), tag: '[SignalQuality.level]', peerId, level, from, force }),
      );
    }
    const delivered = this.deps.send(peerId, level);
    if (delivered === false) return;
    peer.delivered = level;
  }

  private peerFor(peerId: string): PeerSignal {
    let peer = this.peers.get(peerId);
    if (!peer) {
      peer = { window: [], measured: 'none', delivered: null };
      this.peers.set(peerId, peer);
    }
    return peer;
  }
}

/** Lower median of a non-empty list (the smaller middle value for an even count). */
function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}
